// ── Convert-from-builder (whole Case When block) — pure helpers ──────────
// The server (POST /expr-convert-block) does the conversion and refuses
// anything it can't reproduce exactly; this module only decides when the
// button shows, whether to ask before replacing text, and how a response is
// presented. The dropdown cases are never modified by any of this.

// The block config to send, or null when there is nothing to convert: the
// button is only offered when the block has at least one builder case.
export function blockConvertSource(config) {
  const cases = Array.isArray(config?.cases) ? config.cases : [];
  const hasBuilderCase = cases.some(cs => cs && cs.mode !== 'code' && Array.isArray(cs.when) && cs.when.length > 0);
  return hasBuilderCase ? config : null;
}

// One-line hint shown when the editor opens on a block with no saved code.
export const FIRST_OPEN_HINT = 'You have builder rules: use Convert from builder to start from them.';

export function showFirstOpenHint(config, hasSavedCode) {
  return !hasSavedCode && blockConvertSource(config) !== null;
}

// Ask before replacing, except when the box is empty or still the untouched
// starter template (`starter` = the text the editor opened with).
export function needsOverwriteConfirm(currentSrc, starter = '') {
  const cur = String(currentSrc ?? '').trim();
  return cur !== '' && cur !== String(starter ?? '').trim();
}

// Notices the server sends on every conversion are not shown: they describe
// the dropdown's own run warnings and Run Stats, which isn't a difference in
// what the converted block computes. Matched by text prefix/keyword.
const ALWAYS_ON_NOTICE = /^the dropdown['\u2019]s run warnings|run stats are shown per output/i;

export function visibleNotices(notices) {
  return (Array.isArray(notices) ? notices : [])
    .filter(n => typeof n === 'string' && n.trim() !== '' && !ALWAYS_ON_NOTICE.test(n.trim()));
}

export const NOTICES_HEADING = 'Notes about this conversion';

// What the editor renders under the button: null (nothing at all) when there
// is nothing to say, else the heading and the notes.
export function noticesBox(notices) {
  const items = visibleNotices(notices);
  return items.length ? { heading: NOTICES_HEADING, items } : null;
}

// {ok, src, notices[], problems[], message} for the editor. Cases and
// conditions are numbered from 1, as the dropdown rows are shown.
export function mapConvertBlockResponse(resp) {
  if (!resp || typeof resp !== 'object') {
    return { ok: false, src: null, notices: [], problems: [], message: 'The conversion didn\'t return a result.' };
  }
  if (resp.ok && typeof resp.src === 'string' && resp.src.trim() !== '') {
    return {
      ok: true, src: resp.src, problems: [], message: null,
      notices: visibleNotices(resp.notices),
    };
  }
  const problems = (Array.isArray(resp.unsupported) ? resp.unsupported : []).map((u) => {
    const where = [];
    if (Number.isInteger(u.case)) where.push(`Case ${u.case + 1}`);
    if (Number.isInteger(u.condition)) where.push(`condition ${u.condition + 1}`);
    return where.length ? `${where.join(', ')}: ${u.reason}` : String(u.reason);
  });
  return {
    ok: false, src: null, notices: [], problems,
    message: resp.message || (problems.length ? null : 'This block can\'t be converted.'),
  };
}
